import {ssrSafeWindow} from '@github-ui/ssr-utils'
/**
 * Reloads the current page with a reload parameter to avoid infinite loops
 */
export const reloadPage = () => {
  const reloadParameter = ['reload', '1'] as const
  const url = new URL(window.location.href, window.location.origin)

  /**
   * Return early when the parameter already exists to avoid an infinite loop
   */
  if (url.searchParams.has(...reloadParameter)) {
    return
  }

  url.searchParams.set(...reloadParameter)
  // Reload the page
  ssrSafeWindow?.location.assign(url)
}

type RepositoryResult<T> = {ok: true; value: T} | {ok: false; errors: unknown} | null | undefined
type OkayResult<T> = {ok: true; value: T}

/**
 * Handles repository data errors and returns the successful result or undefined
 * @param repositoryData The repository result from a GraphQL query with @catch(to: RESULT)
 * @param options Optional configuration for error handling
 * @returns The successful result with ok: true and value, or undefined if there was an error
 */
export function handleRepositoryDataError<T>(
  repositoryData: RepositoryResult<T>,
  options?: {
    shouldReloadOnAuthError?: boolean
  },
): OkayResult<T> | undefined {
  const {shouldReloadOnAuthError = true} = options || {}

  if (
    !repositoryData ||
    (typeof repositoryData === 'object' && 'ok' in repositoryData && repositoryData.ok === false)
  ) {
    const errors = (repositoryData as {errors?: unknown[]})?.errors

    const hasSAMLError = errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === 'FORBIDDEN')

    const hasAuthenticationError = errors?.some(
      e => e && typeof e === 'object' && 'type' in e && e.type === 'AUTHENTICATION',
    )

    const hasNotFoundError = errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === 'NOT_FOUND')

    // these errors we want to reload since the server should handle redirecting to login or showing a 404 page
    if (shouldReloadOnAuthError && (hasSAMLError || hasAuthenticationError || hasNotFoundError)) {
      reloadPage()
      return undefined
    }
    // we dont have data and its not an auth error, so we throw to let the error boundary handle it
    throw new Error(`Repository data error: ${JSON.stringify(errors)}`)
  }

  return repositoryData as OkayResult<T>
}
